"use client";

import { createContext, type ReactNode, useContext } from "react";
import {
  Radio as RACRadio,
  RadioGroup as RACRadioGroup,
  RadioGroupProps as RACRadioGroupProps,
  RadioProps,
  ValidationResult,
} from "react-aria-components";
import { composeTailwindRenderProps, focusRing } from "../../utils";
import { Description, FieldError, Label } from "../Field";

// Context to pass variant to Radio components
const RadioGroupContext = createContext<{ variant: "default" | "brand" }>({
  variant: "brand",
});

export interface RadioGroupProps extends Omit<RACRadioGroupProps, "children"> {
  /** Label for the radio group */
  label?: ReactNode;
  /** Children elements */
  children?: ReactNode;
  /** Optional description for the radio group */
  description?: string;
  /** Error message to display when validation fails */
  errorMessage?: string | ((validation: ValidationResult) => string);
  /** Validation result object for functional errorMessage */
  validationResult?: ValidationResult;
  /**
   * Visual variant of the radio buttons
   * @default "default"
   */
  variant?: "default" | "brand";
}

export function RadioGroup(props: RadioGroupProps) {
  const { variant = "default", ...otherProps } = props;
  return (
    <RadioGroupContext.Provider value={{ variant }}>
      <RACRadioGroup
        {...otherProps}
        className={composeTailwindRenderProps(props.className, "group flex flex-col gap-2")}
      >
        <Label>{props.label}</Label>
        <div className={`flex ${otherProps.orientation === "horizontal" ? "flex-row gap-4" : "flex-col gap-2"}`}>
          {props.children}
        </div>
        {props.description && <Description>{props.description}</Description>}
        <FieldError>
          {typeof props.errorMessage === "function" && props.validationResult
            ? props.errorMessage(props.validationResult)
            : typeof props.errorMessage === "string"
              ? props.errorMessage
              : null}
        </FieldError>
      </RACRadioGroup>
    </RadioGroupContext.Provider>
  );
}

function getRadioStyles({
  isSelected,
  isInvalid,
  isDisabled,
  variant = "brand",
}: {
  isSelected?: boolean;
  isInvalid?: boolean;
  isDisabled?: boolean;
  variant?: "default" | "brand";
}) {
  const focusRingColor =
    variant === "brand" ? "focus-visible:outline-action-brand" : "focus-visible:outline-action-default";
  const baseClasses = [
    focusRing(),
    focusRingColor,
    "w-5 h-5 rounded-full border-2 bg-background-surface transition-all",
  ];

  if (isDisabled) {
    baseClasses.push("border-border-muted forced-colors:!border-[GrayText]");
  } else if (isInvalid) {
    baseClasses.push(
      "border-feedback-error-border group-pressed:border-feedback-error-border dark:group-pressed:border-feedback-error-border forced-colors:!border-[Mark]"
    );
  } else if (isSelected) {
    const actionColor = variant === "brand" ? "action-brand" : "action-default";
    baseClasses.push(
      `border-[7px] border-${actionColor} forced-colors:!border-[Highlight] group-pressed:border-${actionColor}-hover dark:group-pressed:border-${actionColor}-hover`
    );
  } else {
    baseClasses.push("border-border-default group-pressed:border-border-focus dark:group-pressed:border-border-focus");
  }

  return baseClasses.join(" ");
}

export function Radio(props: RadioProps) {
  const { variant } = useContext(RadioGroupContext);
  return (
    <RACRadio
      {...props}
      className={composeTailwindRenderProps(
        props.className,
        "group flex items-center gap-2 text-sm text-text-body transition disabled:text-text-muted dark:text-text-body dark:disabled:text-text-muted forced-colors:disabled:text-[GrayText]"
      )}
    >
      {(renderProps) => (
        <>
          <div className={getRadioStyles({ ...renderProps, variant })} />
          {props.children}
        </>
      )}
    </RACRadio>
  );
}
